import classnames from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import React, { PropsWithChildren } from 'react';

import { TrainingOffering } from '../../../adl-gen/totalinteraction/futurefit/db';
import { Pill } from '../../widgets/pill/pill';
import { ProgressBar } from '../../widgets/progress-bar/progress-bar';

const styles = require('./tile.css');

export interface RecommendationTileProps {
  offering: TrainingOffering;
  enabled: boolean;
}

/// Recommendations: As we now either display each category in its own
/// tab, we no longer need to echo this information in the Pill.
/// The ideal solution is to display the Competency instead.
/// However, especially for Relevance/Priority recommendations, based on Skill,
/// there is a possibility that more than one competency is associated with the
/// TrainingOffering. If that is the case, consider stashing the Competency
/// in the SkillTrainingOffering. (This still may not solve the issue though)

export type TileProps =
  | {
      kind: 'recommendation';
      imageUrl: string;
      title: string;
      subtitle: string;
      enabled: boolean;
      completed: boolean;
      onClick(): void;
    }
  | {
      kind: 'user-quiz';
      imageUrl: string;
      title: string;
      /// FF2-358 for timestamp (for now)
      subtitle?: string;
      onClick(): void;
      enabled: boolean;
      progress: number;
      total: number;
      index: number;
    }
  | {
      kind: 'manager-report';
      imageUrl: string;
      title: string;
      onClick(): void;
      enabled: boolean;
      progress: number;
      total: number;
    }
  | {
      kind: 'guidance';
      title: string;
      onClick(): void;
      subtitle: string;
      detail: string;
      enabled: boolean;
    }
  | {
      kind: 'report';
      imageUrl: string;
      title: string;
      subtitle: string;
      onClick(): Promise<void>;
      enabled: boolean;
    };

@observer
export class Tile extends React.Component<TileProps> {
  render() {
    let className = styles.tile;
    if (this.props.kind === 'guidance') {
      className = classnames(className, styles.guidance);
    }
    if (!this.props.enabled) {
      className = classnames(className, styles.disabled);
    }
    if (this.props.kind === 'recommendation') {
      if (this.props.completed) {
        className = classnames(className, styles.completed);
      }
    }
    /// FF2-436 set data-tour attribute for walkthrough (looks for index zero)
    let dataTourAttribute;
    if (this.props.kind === 'user-quiz') {
      dataTourAttribute = `tile-image:${this.props.index}`;
    }
    const actionWrapperProps: ActionWrapperProps =
      this.props.kind === 'report'
        ? { kind: 'onClickWait', enabled: this.props.enabled, onClick: this.props.onClick }
        : { kind: 'onClick', enabled: this.props.enabled, onClick: this.props.onClick };
    return (
      <div className={className}>
        <this.renderActionWrapper {...actionWrapperProps}>
          {this.renderHeader()}
          {this.props.kind !== 'guidance' ? (
            <img data-tour={dataTourAttribute} src={this.props.imageUrl} className={styles.image} />
          ) : (
            undefined
          )}
        </this.renderActionWrapper>
      </div>
    );
  }

  // renderActionWrapper = (props: PropsWithChildren<{}>): JSX.Element => {
  renderActionWrapper = (props: PropsWithChildren<ActionWrapperProps>): JSX.Element => {
    return (
      <ActionWrapper
        {...props}
        // kind={this.props.kind === "report" ? "onClickWait" : "onClick"}
        // enabled={this.props.enabled}
        // onClick={this.props.kind !== "report" ? this.props.onClick : undefined}
      >
        {props.children}
      </ActionWrapper>
    );
  };

  renderHeader(): JSX.Element {
    const subtitle =
      /// FF2-358 for user quiz subtitle may be undefined anyway
      this.props.kind === 'recommendation' || this.props.kind === 'guidance' || this.props.kind === 'user-quiz'
        ? this.props.subtitle
        : undefined;
    const index = this.props.kind === 'user-quiz' ? this.props.index : undefined;
    return (
      <div className={styles.header}>
        <p className={styles.title}>{this.props.title}</p>
        {subtitle ? (
          <Pill kind={this.props.kind === 'user-quiz' ? 'overlaid' : 'bordered'}>{subtitle}</Pill>
        ) : (
          undefined
        )}
        {this.renderDetail()}
        {this.renderProgressBar(index)}
      </div>
    );
  }

  renderDetail(): JSX.Element | undefined {
    return this.props.kind === 'guidance' ? <div className={styles.detail}>{this.props.detail}</div> : undefined;
  }

  renderProgressBar(index?: number): JSX.Element | undefined {
    let dataTourAttribute = 'progress-bar';
    if (index !== undefined) {
      dataTourAttribute = `${dataTourAttribute}:${index}`;
    }
    return this.props.kind === 'user-quiz' || this.props.kind === 'manager-report' ? (
      <div>
        <ProgressBar
          progress={this.props.progress}
          total={this.props.total}
          showTooltip={this.props.progress !== this.props.total}
          dataTour={dataTourAttribute}
        />
      </div>
    ) : (
      undefined
    );
  }
}

export type ActionWrapperProps =
  | {
      kind: 'link';
      enabled: boolean;
      link: string;
    }
  | {
      kind: 'onClick';
      enabled: boolean;
      onClick(): void;
    }
  | {
      kind: 'onClickWait';
      enabled: boolean;
      onClick?(): Promise<void>;
      alwaysInProgress?: boolean;
    };

class ActionWrapper extends React.Component<ActionWrapperProps> {
  @observable isInProgress: boolean;

  constructor(props: Readonly<ActionWrapperProps>) {
    super(props);
    this.isInProgress = props.kind === 'onClickWait' ? !!props.alwaysInProgress : false;
  }

  @action private setIsInProgress(inProgress: boolean) {
    this.isInProgress = inProgress;
  }

  /**
   * Click handler which shows a spinner while the action is in progress.
   * If already in progress then callback is not called.
   * Copied from Button
   */
  onClick = async () => {
    if (this.isInProgress) {
      return;
    }
    if (this.props.kind !== 'onClickWait') {
      return;
    }
    if (this.props.onClick === undefined) {
      return;
    } else {
      this.setIsInProgress(true);
      try {
        await this.props.onClick();
      } catch (err) {
        // Rethrow
        throw err;
      } finally {
        this.setIsInProgress(false);
      }
    }
  };

  render() {
    if (!this.props.enabled) {
      return <div>{this.props.children}</div>;
    } else if (this.props.kind === 'link') {
      return <a href={this.props.link}>{this.props.children}</a>;
    } else if (this.props.kind === 'onClick') {
      return (
        <div onClick={this.props.onClick} style={{ cursor: 'pointer' }}>
          {this.props.children}
        </div>
      );
    } else if (this.props.kind === 'onClickWait') {
      return (
        <>
          {this.isInProgress ? <div className={styles.spinner} /> : undefined}
          <div onClick={this.props.onClick} style={{ cursor: 'pointer' }}>
            {this.props.children}
          </div>
        </>
      );
    }
    return <></>;
  }
}
// function ActionWrapper(
//   props: React.PropsWithChildren<ActionWrapperProps>
// ): JSX.Element {
//   if (!props.enabled) {
//     return <div>{props.children}</div>;
//   } else if (props.kind === "link") {
//     return <a href={props.link}>{props.children}</a>;
//   } else if (props.kind === "onClick") {
//     return (
//       <div onClick={props.onClick} style={{ cursor: "pointer" }}>
//         {props.children}
//       </div>
//     );
//   }
//   return <></>;
// }



// WEBPACK FOOTER //
// ./src/ui/components/tile/tile.tsx